import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Container,
  Paper,
  TextField,
  CircularProgress,
  Alert,
  Typography,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import { useAuth } from '../hooks/useAuth';
import { findHotelByCode } from '../services/hotelService';
import { Hotel } from '../models/Hotel';
import { setSelectedHotel } from '../services/api';
import LanguageSelector from '../components/LanguageSelector';


export const Login = () => {
  const [hotelCode, setHotelCode] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const { login, loading } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Load saved credentials on component mount
  useEffect(() => {
    const savedHotelCode = localStorage.getItem('hotelCode');
    const savedUsername = localStorage.getItem('username');
    const savedRememberMe = localStorage.getItem('rememberMe') === 'true';
    
    if (savedRememberMe && savedHotelCode && savedUsername) {
      setHotelCode(savedHotelCode);
      setUsername(savedUsername);
      setRememberMe(true);
    }
  }, []);
  
  // Validate hotel code format (6 digits)
  const validateHotelCode = (code: string): boolean => {
    return /^\d{6}$/.test(code);
  };
  
  // Find hotel by code from Firebase
  const setupHotelConnection = async (code: string): Promise<Hotel | null> => {
    try {
      // Find hotel using the hotel service (now uses Firebase only)
      const hotel = await findHotelByCode(code);
      
      if (hotel) {
        // Set the selected hotel for this session
        setSelectedHotel(hotel);
        return hotel;
      }
      
      return null;
    } catch (error) {
      console.error('Error finding hotel by code:', error);
      return null;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate inputs
    if (!hotelCode.trim()) {
      setError(t('login.errors.hotelCodeRequired'));
      return;
    }

    if (!validateHotelCode(hotelCode.trim())) {
      setError(t('login.errors.hotelCodeInvalid'));
      return;
    }

    if (!username.trim()) {
      setError(t('login.errors.usernameRequired'));
      return;
    }

    if (!password.trim()) {
      setError(t('login.errors.passwordRequired'));
      return;
    }

    try {
      // Find hotel by code and setup connection
      const hotel = await setupHotelConnection(hotelCode.trim());
      
      if (!hotel) {
        setError(t('login.errors.hotelNotFound'));
        return;
      }

      console.log("Attempting login with:", { username, hotelCode: hotelCode.trim(), hotelId: hotel.id });
      
      await login({ 
        username: username.trim(), 
        password,
        hotelId: hotel.id
      });
      
      console.log("AuthContext login completed, saving preferences...");
      
      // Save credentials if remember me is checked
      if (rememberMe) {
        localStorage.setItem('hotelCode', hotelCode.trim());
        localStorage.setItem('username', username.trim());
        localStorage.setItem('rememberMe', 'true');
      } else {
        // Clear saved credentials if remember me is unchecked
        localStorage.removeItem('hotelCode');
        localStorage.removeItem('username');
        localStorage.removeItem('rememberMe');
      }
      
      console.log("Login successful, redirecting to zones...");
      
      // Navigate to zones page directly to avoid redirect loop
      navigate("/zones");
    } catch (err: any) {
      console.error("Login error:", err);
      
      if (err.message && err.message.includes('offline')) {
        setError(t('login.errors.serverOffline'));
      } else {
        setError(err.response?.data || err.message || t('login.errors.loginFailed'));
      }
    }
  };
  
  const handleHotelCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6); // Only digits, max 6
    setHotelCode(value);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 2,
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Background Pattern */}
      <Box sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'radial-gradient(circle at 50% 50%, rgba(46, 134, 171, 0.05) 0%, transparent 50%)',
        zIndex: 1
      }} />
      <Container component="main" maxWidth="xs" sx={{ position: 'relative', zIndex: 2 }}>
        <Paper 
          elevation={0} 
          sx={{ 
            p: 4,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(46, 134, 171, 0.1)',
            borderRadius: '24px',
            boxShadow: '0 20px 40px rgba(15, 23, 42, 0.08), 0 8px 16px rgba(15, 23, 42, 0.04)',
            color: '#0f172a',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
          }}
        >
          {/* Language Selector + Back to Home - Inside card at top */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <LanguageSelector />
            <Button
              variant="text"
              startIcon={<HomeIcon />}
              onClick={() => window.location.href = '/'}
              sx={{
                color: 'rgba(15, 23, 42, 0.7)',
                textTransform: 'none',
                fontWeight: 500,
                borderRadius: '12px',
                px: 2.5,
                py: 1,
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: 'rgba(46, 134, 171, 0.08)',
                  color: 'rgba(15, 23, 42, 0.9)'
                }
              }}
            >
              {t('login.backToHome')}
            </Button>
          </Box>

          {/* Logo Section - Inside card */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
            <Box 
              component="img"
              src="/rafilis-mp-logo.png"
              alt="Multizone Player Logo"
              sx={{ 
                width: 160,
                height: 'auto',
                filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3))'
              }}
            />
          </Box>
          
          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 2,
                background: 'rgba(239, 68, 68, 0.05)',
                border: '1px solid rgba(239, 68, 68, 0.2)',
                color: '#dc2626',
                borderRadius: '12px',
                '& .MuiAlert-icon': {
                  color: '#dc2626'
                }
              }}
            >
              {error}
            </Alert>
          )}
          
          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              margin="normal"
              required
              fullWidth
              id="hotelCode"
              label={t('login.hotelCode')}
              name="hotelCode"
              placeholder="123456"
              value={hotelCode}
              onChange={handleHotelCodeChange}
              disabled={loading}
              inputProps={{ 
                maxLength: 6,
                style: { 
                  textTransform: 'uppercase',
                  fontFamily: 'monospace',
                  fontSize: '1.2rem',
                  textAlign: 'center',
                  letterSpacing: '0.2em'
                }
              }}
              helperText={t('login.hotelCodeHelper')}
              sx={{
                mb: 1.5,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#f8fafc',
                  borderRadius: '12px',
                  '& fieldset': {
                    borderColor: 'rgba(46, 134, 171, 0.2)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(46, 134, 171, 0.4)'
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#2e86ab',
                    borderWidth: '2px'
                  },
                  '& input': {
                    color: '#0f172a'
                  }
                },
                '& .MuiInputLabel-root': {
                  color: 'rgba(15, 23, 42, 0.7)',
                  '&.Mui-focused': {
                    color: '#2e86ab'
                  }
                },
                '& .MuiFormHelperText-root': {
                  color: 'rgba(15, 23, 42, 0.6)'
                }
              }}
            />
            
            <TextField
              margin="normal"
              required
              fullWidth
              id="username"
              label={t('login.username')}
              name="username"
              autoComplete="username"
              autoFocus
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={loading}
              sx={{
                mb: 1.5,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#f8fafc',
                  borderRadius: '12px',
                  '& fieldset': {
                    borderColor: 'rgba(46, 134, 171, 0.2)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(46, 134, 171, 0.4)'
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#2e86ab',
                    borderWidth: '2px'
                  },
                  '& input': {
                    color: '#0f172a'
                  }
                },
                '& .MuiInputLabel-root': {
                  color: 'rgba(15, 23, 42, 0.7)',
                  '&.Mui-focused': {
                    color: '#2e86ab'
                  }
                }
              }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label={t('login.password')}
              type="password"
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#f8fafc',
                  borderRadius: '12px',
                  '& fieldset': {
                    borderColor: 'rgba(46, 134, 171, 0.2)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(46, 134, 171, 0.4)'
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#2e86ab',
                    borderWidth: '2px'
                  },
                  '& input': {
                    color: '#0f172a'
                  }
                },
                '& .MuiInputLabel-root': {
                  color: 'rgba(15, 23, 42, 0.7)',
                  '&.Mui-focused': {
                    color: '#2e86ab'
                  }
                }
              }}
            />
            
            <FormControlLabel
              control={
                <Checkbox
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  sx={{
                    color: 'rgba(15, 23, 42, 0.6)',
                    '&.Mui-checked': {
                      color: '#2e86ab'
                    }
                  }}
                />
              }
              label={
                <Typography sx={{
                  color: 'rgba(15, 23, 42, 0.8)',
                  fontFamily: '"Inter", sans-serif',
                  fontSize: '0.9rem'
                }}>
                  {t('login.rememberMe')}
                </Typography>
              }
              sx={{ mt: 0.5, mb: 2 }}
            />
            
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading || !hotelCode.trim() || !username.trim() || !password.trim()}
              sx={{ 
                mt: 1.5, 
                mb: 1.5,
                py: 1.5,
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #2e86ab 0%, #4a90c2 100%)',
                color: '#ffffff',
                fontWeight: 600,
                fontSize: '1rem',
                fontFamily: '"Inter", sans-serif',
                textTransform: 'none',
                boxShadow: '0 8px 20px rgba(46, 134, 171, 0.3)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.2) 0%, transparent 50%)',
                  transition: 'opacity 0.3s ease',
                  opacity: 0
                },
                '&:hover': {
                  background: 'linear-gradient(135deg, #266b8a 0%, #3d7ba3 100%)',
                  boxShadow: '0 12px 28px rgba(46, 134, 171, 0.4)',
                  transform: 'translateY(-2px)',
                  '&::before': {
                    opacity: 1
                  }
                },
                '&:active': {
                  transform: 'translateY(0px)',
                  boxShadow: '0 6px 16px rgba(46, 134, 171, 0.3)'
                },
                '&:disabled': {
                  background: 'rgba(46, 134, 171, 0.2)',
                  color: 'rgba(15, 23, 42, 0.4)',
                  boxShadow: 'none',
                  transform: 'none'
                }
              }}
            >
              {loading ? (
                <CircularProgress 
                  size={24} 
                  sx={{ 
                    color: '#ffffff' 
                  }} 
                />
              ) : (
                t('login.signIn')
              )}
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};